import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { TextArea } from './ui/TextArea';
import type { QuestionnaireData } from '../types';

interface ObjectionStepperProps {
  onAllComplete: () => void;
}

const OBJECTION_HINTS = {
  politeReason: {
    label: 'What they SAY',
    placeholder:
      '"I need to think about it" / "I want to talk to my wife" / "Now\'s not a good time"',
    hint: 'The polite excuse — what do they tell you to your face?',
  },
  realReason: {
    label: 'What they\'re ACTUALLY thinking',
    placeholder:
      '"What if annuities are a scam?" / "What if I lose access to my money?" / "What if the market recovers and I missed out?"',
    hint: 'The fear they won\'t say out loud',
  },
};

export function ObjectionStepper({
  onAllComplete,
}: ObjectionStepperProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const { register, watch } = useFormContext<QuestionnaireData>();

  const handleNext = () => {
    if (activeIndex < 2) {
      setActiveIndex(activeIndex + 1);
    } else {
      onAllComplete();
    }
  };

  const handleBack = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mini progress indicator */}
      <div className="flex items-center gap-3">
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === activeIndex
                  ? 'bg-primary-400'
                  : i < activeIndex
                  ? 'bg-primary-400/40'
                  : 'bg-slate-600'
              }`}
            />
          ))}
        </div>
        <span className="text-sm text-slate-400">
          Objection {activeIndex + 1} of 3
        </span>
      </div>

      {/* Animated objection card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeIndex}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.25 }}
          className="p-5 bg-slate-800/50 rounded-xl space-y-5"
        >
          <div>
            <TextArea
              {...register(
                `question3.objections.${activeIndex}.politeReason`
              )}
              id={`politeReason-${activeIndex}`}
              label={OBJECTION_HINTS.politeReason.label}
              placeholder={OBJECTION_HINTS.politeReason.placeholder}
              value={watch(
                `question3.objections.${activeIndex}.politeReason`
              )}
              className="min-h-[80px]"
            />
          </div>

          <div>
            <TextArea
              {...register(
                `question3.objections.${activeIndex}.realReason`
              )}
              id={`realReason-${activeIndex}`}
              label={OBJECTION_HINTS.realReason.label}
              placeholder={OBJECTION_HINTS.realReason.placeholder}
              value={watch(
                `question3.objections.${activeIndex}.realReason`
              )}
              className="min-h-[80px]"
            />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Internal navigation */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={handleBack}
          className={`text-sm text-slate-400 hover:text-slate-200 transition-colors ${
            activeIndex === 0 ? 'invisible' : ''
          }`}
        >
          ← Previous objection
        </button>

        <Button
          type="button"
          onClick={handleNext}
          variant="secondary"
          className="min-w-[160px]"
        >
          {activeIndex < 2 ? 'Next Objection' : 'Last One'}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
